/**
 * Path to Performance Constants
 * Centralized constants for P2P feature to avoid duplication
 */

/**
 * Category code mapping: Maps user-friendly category names to API category type codes
 * Used across ActionItemTable, PathPerformance, and pathToPerformanceSlice
 */
export const CATEGORY_TYPE_MAP: Record<string, string> = {
  Sponsorship: "S",
  Leadership: "L",
  Training: "T",
  "Team Process": "P",
  "Team Engagement": "E",
  "Use of Tools": "U",
  "Goals and Performance": "G",
} as const;

/**
 * Reverse mapping: API category codes to user-friendly names
 */
export const CATEGORY_NAME_MAP: Record<string, string> = {
  S: "Sponsorship",
  L: "Leadership",
  T: "Training",
  P: "Team Process",
  E: "Team Engagement",
  U: "Use of Tools",
  G: "Goals and Performance",
} as const;

/**
 * Category type codes (type-safe union)
 */
export type CategoryTypeCode = "S" | "L" | "T" | "P" | "E" | "U" | "G";

/**
 * Default dropdown option for action needed
 */
export const DEFAULT_ACTION_OPTION = "Select Next Action Item";

/**
 * Special rating values
 */
export const RATING_NOT_APPLICABLE = 99;
export const RATING_UNRATED = 0;

/**
 * Valid rating range (1-5)
 */
export const RATING_MIN = 1;
export const RATING_MAX = 5;

/**
 * Rating level color scheme for visual indicators
 * Maps rating levels to Tailwind CSS color classes
 */
export const RATING_COLORS = {
  1: "bg-[#D00000]", // Red - Level 1 (Lowest performance)
  2: "bg-[#F36C0D]", // Orange - Level 2
  3: "bg-[#FEC941]", // Yellow - Level 3
  4: "bg-[#0074AD]", // Blue - Level 4
  5: "bg-[#33A437]", // Green - Level 5 (Highest performance)
  99: "bg-gray-500", // Gray - Not Applicable
  0: "bg-gray-300",  // Default gray for unrated
} as const;

/**
 * Rating level descriptions for accessibility and documentation
 */
export const RATING_LABELS = {
  1: "Level 1 - Lowest performance",
  2: "Level 2 - Below average",
  3: "Level 3 - Average",
  4: "Level 4 - Above average",
  5: "Level 5 - Highest performance",
  99: "Not Applicable",
  0: "Unrated",
} as const;

/**
 * Valid rating type (union of valid values)
 */
export type RatingValue = 0 | 1 | 2 | 3 | 4 | 5 | 99;

/**
 * Maximum character lengths for text inputs
 */
export const MAX_NOTES_LENGTH = 2048;

/**
 * Date format constants
 */
export const DATE_FORMAT_DISPLAY = "MM-DD-YYYY";
export const DATE_FORMAT_API = "YYYY-MM-DD";

/**
 * Authorization levels
 */
export const AUTHORIZATION_LEVEL = {
  EDIT: "E",
  VIEW: "V",
  FORBIDDEN: "F",
} as const;

/**
 * Huddles usage options
 */
export const HUDDLES_OPTIONS = {
  YES: "Yes",
  NO: "No",
  API_YES: "Y",
  API_NO: "N",
} as const;

/**
 * Assessment period constants
 */
export const ASSESSMENT_PERIODS = {
  CURRENT: "Current",
  PREVIOUS: "Previous",
} as const;

/**
 * Category names (ordered array)
 */
export const CATEGORY_NAMES = [
  "Sponsorship",
  "Leadership",
  "Training",
  "Team Process",
  "Team Engagement",
  "Use of Tools",
  "Goals and Performance",
] as const;

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = {
  P2P_SAVED: "Path to Performance data saved successfully!",
  NO_CHANGES: "There are no changes to be saved!",
} as const;

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
  NO_ACTION_SELECTED: "Please select Action Needed drop down for at least one category to provide rating for the team.",
  INVALID_DATE_FORMAT: "Invalid date format. Please use MM-DD-YYYY format.",
  LAST_FACE_TO_FACE_REQUIRED: "Last Face to Face Assessment date is required.",
  FAILED_TO_SAVE: "Failed to save data. Please try again.",
  UNAUTHORIZED: "You are not authorized to access the path to performance page.",
  NO_TEAM_SELECTED: "Please select an UBT before accessing Path to Performance (P2P) page OR Team Documents page. You can access the page from Select UBT/ My Teams/ Team Details page etc.",
} as const;

/**
 * Notification messages
 */
export const NOTIFICATION_MESSAGES = {
  LEAVE_WITHOUT_SAVING: "If you leave the page without saving, your changes will be lost.",
  SELECT_UBT_BEFORE_ACCESS: "Please select an UBT",
} as const;

/**
 * Field ID generators for consistent IDs
 */
export const FIELD_IDS = {
  ACTION_NOTES: (id: string) => `action-notes-${id}`,
  ASSESSMENT_NOTES: (id: string) => `assessment-notes-${id}`,
  ACTION_COUNTER: (id: string) => `${id}-counter`,
} as const;

/**
 * Aria labels for accessibility
 */
export const ARIA_LABELS = {
  NOTES_TEXTAREA: "notes-textarea",
  ASSESSMENT_NOTES_TEXTAREA: "assessment-notes-textarea",
  RATING: (rating: number, label: string) => `Rating: ${rating} - ${label}`,
} as const;

/**
 * Auto-dismiss durations (milliseconds)
 */
export const AUTO_DISMISS_DURATION = {
  SUCCESS: 5000,
  ERROR: 5000,
  VALIDATION: Infinity, // Never auto-dismiss validation errors
} as const;
